import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import CartDrawer from "./components/CartDrawer";
import PresenceTracker from "./components/PresenceTracker";
import { initPostHog } from "./lib/posthog";
import Layout from "./components/Layout";
import ResearcherGate from "./components/ResearcherGate";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import COA from "./pages/COA";
import ProductDetail from "./pages/ProductDetail";
import Affiliate from "./pages/Affiliate";
import LegalPage from "./pages/LegalPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Account from "./pages/Account";
import { useAuth } from "./context/AuthContext";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import SimplePage from "./pages/SimplePage";

function GateGuard() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [verified, setVerified] = useState(() => {
    try {
      const raw = localStorage.getItem("ethos_verified");
      if (!raw) return false;
      const { at } = JSON.parse(raw);
      // Valid for 30 days, then re-confirm.
      return typeof at === "number" && Date.now() - at < 30 * 24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  });
  // Auth + admin screens must be reachable before attestation so returning users can sign in.
  const whitelisted = ["/login", "/register", "/forgot-password", "/reset-password", "/admin"].some((p) => pathname === p || pathname.startsWith("/admin"));
  if (verified || whitelisted || (user && user.email)) return null;
  return <ResearcherGate onVerified={() => setVerified(true)} />;
}

function App() {
  useEffect(() => { initPostHog(); }, []);
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollToTop />
            <PresenceTracker />
            <GateGuard />
            <CartDrawer />
            <Routes>
              <Route path="/" element={<Layout heavenly><Home /></Layout>} />
              <Route path="/shop" element={<Layout><Shop /></Layout>} />
              <Route path="/coa" element={<Layout><COA /></Layout>} />
              <Route path="/product/:id" element={<Layout><ProductDetail /></Layout>} />
              <Route path="/checkout" element={<Layout promo={false}><Checkout /></Layout>} />
              <Route path="/order/:orderNumber" element={<Layout promo={false}><OrderConfirmation /></Layout>} />
              <Route path="/faq" element={<Layout><SimplePage page="faq" /></Layout>} />
              <Route path="/contact" element={<Layout><SimplePage page="contact" /></Layout>} />
              <Route path="/affiliates" element={<Layout><Affiliate /></Layout>} />
              <Route path="/track" element={<Layout><SimplePage page="track" /></Layout>} />
              <Route path="/terms" element={<Layout><LegalPage page="terms" /></Layout>} />
              <Route path="/privacy" element={<Layout><LegalPage page="privacy" /></Layout>} />
              <Route path="/shipping" element={<Layout><LegalPage page="shipping" /></Layout>} />
              <Route path="/login" element={<Layout promo={false}><Login /></Layout>} />
              <Route path="/register" element={<Layout promo={false}><Register /></Layout>} />
              <Route path="/forgot-password" element={<Layout promo={false}><ForgotPassword /></Layout>} />
              <Route path="/reset-password" element={<Layout promo={false}><ResetPassword /></Layout>} />
              <Route path="/account" element={<Layout promo={false}><Account /></Layout>} />
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
